// hooks
import { useEffect, useState } from 'react';
import { useSelector } from 'hooks';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';
import useAutomaticRosteringDelivery from '../automatic-rostering/hooks/useAutomaticRosteringDelivery';
import useRosterPlanMode from './useRosterPlanMode';
import { useSelectedPlanUnits } from '../RosterPlan.stores';

// types
import type { RootState } from 'redux/rootReducer';
import type { AutomaticRosteringActionsProps } from '../automatic-rostering/AutomaticRosteringActions/AutomaticRosteringActions.types';
import type { AutomaticRosteringExplainPanelProps } from '../automatic-rostering/AutomaticRosteringExplainPanel/AutomaticRosteringExplainPanel.types';

// utils
import dayjs from 'dayjs';
import {
  getAutomaticRosteringBlockReason,
  getSelectedOrganizationalUnitCode,
  isAutomaticRosteringExplainable
} from '../automatic-rostering';
import { getRosterPlanModeLayout } from '../utils/getRosterPlanModeLayout';

type UseAutomaticRosteringToolbarProps = {
  /** The displayed period, from `useRosterPlanQueryParams`. */
  startDate: Date;
  endDate: Date;
};

type UseAutomaticRosteringToolbarResult = {
  /** Props for the toolbar actions, or `null` without the module or outside plan mode. */
  actions: AutomaticRosteringActionsProps | null;
  /** The explanation panel's `key` and props, or `null` while it is closed. */
  explainPanel: {
    key: string;
    props: AutomaticRosteringExplainPanelProps;
  } | null;
};

/**
 * Wires the automatic rostering feature into the roster screen: the calculation for the week on
 * display, the explanation panel's open state, and the reason generating is blocked.
 *
 * No `useMemo`, unlike the derivation hooks in this folder: `useAutomaticRosteringDelivery` hands
 * back a new `run` and `startRun` on every render, so a memo over them would never hit. The cost is
 * that `actions` is new on every render too, so each status poll (every 5 s while the engine
 * calculates) re-renders `RosterPlanToolbar`. That is the toolbar, not a grid, and it did so before
 * this hook existed; the fix is a stable `run` in `useAutomaticRosteringDelivery`, not a memo here.
 */
const useAutomaticRosteringToolbar = ({
  startDate,
  endDate
}: UseAutomaticRosteringToolbarProps): UseAutomaticRosteringToolbarResult => {
  const { hasAutomaticRosteringModule, hasRosterPortalPlanModusAccess } =
    useSelector((state: RootState) => state.grants);
  const period = useDateNavigationStore((state) => state.period);
  // Plan mode's tools (#2499): outside it the actions go and the panel closes. The engine is still
  // read, so a run started in plan mode refreshes the grids when it ends while the planner views.
  const { showsPlanActions } = getRosterPlanModeLayout(
    useRosterPlanMode().mode
  );
  const isAvailable = hasAutomaticRosteringModule && showsPlanActions;

  // The engine is addressed by organizational unit code and rosters one unit at a time; anything
  // else and the toolbar blocks the action rather than guessing which unit the planner meant.
  const selectedPlanUnits = useSelectedPlanUnits();
  const organizationalUnitCode =
    getSelectedOrganizationalUnitCode(selectedPlanUnits);
  const isoStartDate = dayjs(startDate).format('YYYY-MM-DD');
  const isoEndDate = dayjs(endDate).format('YYYY-MM-DD');

  const { run, startRun, hasFailedToStart, isStatusPending } =
    useAutomaticRosteringDelivery({
      organizationalUnitCode,
      startDate: isoStartDate,
      endDate: isoEndDate,
      enabled: hasAutomaticRosteringModule && period === 'week'
    });

  const [isExplainPanelOpen, setIsExplainPanelOpen] = useState(false);

  // A week nobody generated has nothing to explain, so the action is off and an open panel closes.
  // Navigating to such a week with the panel open would otherwise leave it saying only that.
  const canExplain = isAutomaticRosteringExplainable(run.state);

  // Closing waits for the status of the week on screen: right after navigating, its first read is
  // still out and the state reads `idle`, which would close the panel on its way to a week that does
  // have a calculation. Anything else without a calculation closes it - a read `NOT_STARTED`, a read
  // that failed, or a screen that asks for no status at all (another period, not exactly one unit).
  // Leaving plan mode closes it too, so coming back does not reopen a conversation left behind.
  const hasNothingToExplain =
    !showsPlanActions || (!canExplain && !isStatusPending);
  useEffect(() => {
    if (hasNothingToExplain) setIsExplainPanelOpen(false);
  }, [hasNothingToExplain]);

  // Generating opens the explanation panel, which follows the calculation step by step and turns into
  // the chat once it is delivered. The run reports `starting` until the engine has picked it up, so
  // the effect above does not close the panel again on the stale status of the moment before.
  const generate = () => {
    startRun();
    setIsExplainPanelOpen(true);
  };

  // Reading the state and asking for an explanation need the module and ROSTER_PORTAL; starting a
  // calculation writes a base plan and needs ROSTER_PORTAL_ROSTER_PLAN_MODUS on top. The button
  // carries that difference rather than letting the endpoint refuse a click it invited.
  const blockReason = getAutomaticRosteringBlockReason({
    period,
    organizationalUnitCode,
    runState: run.state,
    canStart: hasRosterPortalPlanModusAccess
  });

  const actions = isAvailable
    ? {
        run,
        blockReason,
        hasFailedToStart,
        onGenerate: generate,
        canExplain,
        isExplainOpen: isExplainPanelOpen,
        onToggleExplain: () => setIsExplainPanelOpen((isOpen) => !isOpen)
      }
    : null;

  const explainPanel =
    isExplainPanelOpen && showsPlanActions
      ? {
          // The conversation belongs to one unit and one week: the engine answers from the data it
          // froze for that calculation, so carrying the turns to another week would hand it context
          // about a roster it is not being asked about. Remounting drops them with the panel state.
          key: `${organizationalUnitCode}-${isoStartDate}`,
          props: {
            run,
            organizationalUnitCode,
            startDate: isoStartDate,
            endDate: isoEndDate,
            subtitle: `${selectedPlanUnits[0]?.name ?? ''} · ${dayjs(
              startDate
            ).format('D MMM')} - ${dayjs(endDate).format('D MMM YYYY')}`,
            onClose: () => setIsExplainPanelOpen(false)
          }
        }
      : null;

  return { actions, explainPanel };
};

export default useAutomaticRosteringToolbar;
